import { Box } from '@mui/material'
import { css } from '@emotion/react'
import styled from '@emotion/styled'

export const Container = styled(Box)`
  ${({ theme }) => css`
    margin-top: 24px;
    border-radius: 16px;
    border: 1px solid ${theme.colors.border.light};
    background: ${theme.colors.lightest};
    box-shadow: 4px 4px 8px 0 ${theme.colors.background.hover};
    overflow: hidden;
  `}
`

export const HeaderRow = styled(Box)`
  ${({ theme }) => css`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    border-bottom: 2px solid ${theme.colors.secondary};
  `}
`

export const HeaderCell = styled(Box, { shouldForwardProp: (prop) => prop !== 'isLast' })<{ isLast?: boolean }>`
  ${({ theme, isLast }) => css`
    padding: 12px 16px;
    text-align: center;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    color: ${theme.colors.secondary};
    background: ${theme.colors.gray[100]};
    border-right: ${isLast ? 'none' : `1px solid ${theme.colors.border.light}`};
  `}
`

export const BodyRow = styled(Box)`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
`

export const BodyColumn = styled(Box, { shouldForwardProp: (prop) => prop !== 'isLast' })<{ isLast?: boolean }>`
  ${({ theme, isLast }) => css`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 16px;
    border-right: ${isLast ? 'none' : `1px solid ${theme.colors.border.light}`};
  `}
`

export const StatusLabel = styled(Box)`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  color: var(--status-label-color);
  transition: color 0.15s ease;
`

export const CountBadge = styled(Box)`
  font-size: 12px;
  font-weight: 700;
  border-radius: 6px;
  padding: 2px 8px;
  min-width: 20px;
  text-align: center;
  color: var(--status-badge-color);
  background: var(--status-badge-background);
  transition:
    color 0.15s ease,
    background 0.15s ease;
`

export const SkeletonStatusRow = styled(Box)`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 8px 12px;
    border-radius: 8px;
    background: ${theme.colors.gray[100]};
  `}
`

export const SkeletonLine = styled.div<{ width?: string; height?: string }>`
  ${({ theme, width = '100%', height = '16px' }) => css`
    width: ${width};
    height: ${height};
    background: linear-gradient(
      90deg,
      ${theme.colors.border.light} 25%,
      #f0f0f0 50%,
      ${theme.colors.border.light} 75%
    );
    background-size: 200% 100%;
    animation: skeleton-loading 1.5s infinite;
    border-radius: 4px;

    @keyframes skeleton-loading {
      0% {
        background-position: 200% 0;
      }
      100% {
        background-position: -200% 0;
      }
    }
  `}
`

const STATUS_ROW_PROPS = ['background', 'hoverBackground', 'color', 'badgeColor', 'badgeBackground']

export const StatusRow = styled(Box, {
  shouldForwardProp: (prop) => !STATUS_ROW_PROPS.includes(prop as string)
})<{
  background: string
  hoverBackground: string
  color: string
  badgeColor: string
  badgeBackground: string
}>`
  ${({ background, hoverBackground, color, badgeColor, badgeBackground }) => css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 8px 12px;
    border-radius: 8px;
    cursor: pointer;
    background: ${background};
    transition: background 0.15s ease;

    --status-label-color: ${color};
    --status-badge-color: ${badgeColor};
    --status-badge-background: ${badgeBackground};

    &:hover {
      background: ${hoverBackground};
      --status-label-color: #ffffff;
      --status-badge-color: #ffffff;
      --status-badge-background: rgba(0, 0, 0, 0.15);
    }
  `}
`
